"use client";

import styles from "./TemplateDetailPageHeader.module.css";
import { Text } from "@zapier/design-system";
import { useInView } from "react-intersection-observer";
import { useTranslations } from "next-intl";
import {
  type Template,
  TemplateHeroSection,
  TemplateCTAs,
} from "@zapier/templates";
import { useTemplateBookmarkContext } from "src/contexts/TemplateBookmarkContext";
import { useTemplateExecutionContext } from "src/contexts/TemplateExecutionContext";

type TemplateDetailPageHeaderProps = {
  template: Template;
  isLoggedIn: boolean;
  isDraftModeEnabled: boolean;
};

export const TemplateDetailPageHeader = ({
  template,
  isLoggedIn,
  isDraftModeEnabled,
}: TemplateDetailPageHeaderProps) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    initialInView: true,
    fallbackInView: true,
  });
  const {
    createUserTemplateBookmark,
    deleteUserTemplateBookmark,
    isBookmarked,
    templateBookmarkChanging,
  } = useTemplateBookmarkContext();

  const {
    createTemplate,
    creationScreen,
    error: creationError,
    setError: setCreationError,
  } = useTemplateExecutionContext();

  const isStickyHeader = !inView;
  const t = useTranslations("TemplateDetail");

  const stickyHeader = (
    <div className={styles.stickyHeader}>
      <div className={styles.content}>
        <Text tag="h1" type="ParagraphHeader3" className={styles.title}>
          {template.name} {isDraftModeEnabled && t("draftLabel")}
        </Text>
        <TemplateCTAs
          template={template}
          isLoggedIn={isLoggedIn}
          hasBookmarking={isLoggedIn}
          templateBookmarkActions={{
            onSave: async (id: string) => {
              await createUserTemplateBookmark(id);
            },
            onUnsave: deleteUserTemplateBookmark,
            isBookmarked: Boolean(template.id && isBookmarked(template.id)),
            isLoading: templateBookmarkChanging === template.id,
          }}
          tryTemplateActions={{
            hasCreationScreen: !!creationScreen,
            createTemplate: createTemplate,
            creationError: creationError,
            setCreationError: setCreationError,
          }}
          buttonSize={isStickyHeader && !isLoggedIn ? "small" : "medium"}
          hasTemplatePage={true}
        />
      </div>
    </div>
  );

  return (
    <div ref={ref}>
      {isStickyHeader && stickyHeader}
      <TemplateHeroSection
        template={template}
        isLoggedIn={isLoggedIn}
        hasBookmarking={isLoggedIn}
        isDraftModeEnabled={isDraftModeEnabled}
        tryTemplateActions={{
          hasCreationScreen: !!creationScreen,
          createTemplate: createTemplate,
          creationError: creationError,
          setCreationError: setCreationError,
        }}
        templateBookmarkActions={{
          onSave: async (id: string) => {
            await createUserTemplateBookmark(id);
          },
          onUnsave: deleteUserTemplateBookmark,
          isBookmarked: Boolean(template.id && isBookmarked(template.id)),
          isLoading: templateBookmarkChanging === template.id,
        }}
        hasTemplatePage={true}
      />
    </div>
  );
};
